import { InformationContainer } from '@features/common';
import { Cities } from '@features/customers/interfaces/search.interfaces';
import {
	AGWGuidesRangeFormType,
	DivisionEditionFormType,
} from '@features/divisions/interfaces/division-edition.interface';
import {
	generateFinalConsecutiveRules,
	INITIAL_CONSECUTIVE_RULES,
} from '@features/processes/constants/process-forms.constants';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Autocomplete, Button, Chip, Grid, IconButton, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
	handleProcessNumberKeyPress,
	handleProcessNumberPaste,
} from '../../processes/components/ProcessEditionForm/ProcessEditionForm.handlers';
import ViewMessageSyncUp from '@features/common/ViewMessageSyncUp/ViewMessageSyncUp';
import ViewMessageDelete from '@features/common/ViewMessageDelete/ViewMessageDelete';
import SightLoading from '@features/common/Loading/SightLoading';
import useRequests from '@hooks/requests.hook';
import { useSnackbar } from 'notistack';

interface Props {
	data: AGWGuidesRangeFormType;
	cities: Cities[];
	form: UseFormReturn<DivisionEditionFormType>;
	index: number;
	nit: string;
	codigoDiv: string;
}

const AGWRangeHistoryCard: React.FC<Props> = ({
	data,
	cities,
	form: { control, setValue, watch, setError },
	index,
}) => {
	// States
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isClientOpen, setIsClientOpen] = useState<boolean>(false);
	const [isTest, setIsTest] = useState<boolean>(false);
	const [isClose, setIsClose] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [errorResponse, setErrorResponse] = useState<boolean>(false);
	const [dataResponse, setDataResponse] = useState<any>({});
	const { requestAdministraCliente } = useRequests();
	const { enqueueSnackbar } = useSnackbar();

	//const [city, setCity] = useState<null | string>(null);
	// Variables
	const consecutivoDesde = Number(watch(`agw_ranges.${index}.consecutivo_desde`)) || 0;
	useEffect(() => {
		const wheel = document?.getElementsByClassName('numberInput');
		for (let index = 0; index < wheel.length; index++) {
			wheel[index].addEventListener(
				'wheel',
				function (e) {
					e.preventDefault();
				},
				{ passive: false }
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const calculateValue = (value: string | undefined): Cities | undefined => {
		return cities.find((city) => city.codigo.toString() === value);
	};

	const closeCardtest = () => {
		setIsTest(false);
	};
	const closeCardClose = () => {
		setIsClose(false);
	};

	const sincronizarRangosTest = async () => {
		setLoading(true);
		try {
			const response = (await requestAdministraCliente(
				'rangos/gateway/migracion-test',
				'POST',
				{
					id_rango: data.id_rango,
				}
			)) as any;

			// se valida la respuesta negativa de la petición
			if (response.isError === true) {
				setDataResponse(response.data);
				setErrorResponse(true);
				setLoading(false);
				setIsTest(true);
				// se envian datos de respuesta a ventana emergente de error y se muestran
			}

			if (response.isError === false) {
				setErrorResponse(false);
				setLoading(false);
				setIsTest(true);
			}
		} catch (error: any) {
			setLoading(false);
			if (error.response && error.response.data.message) {
				enqueueSnackbar(error.response.data.message, { variant: 'error' });
			} else if (typeof error?.message === 'string') {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		}
	};

	return (
		<>
			{isTest && (
				<ViewMessageSyncUp
					onClose={closeCardtest}
					errorResponse={errorResponse}
					dataResponse={dataResponse}
				/>
			)}
			{isClose && (
				<ViewMessageDelete
					onClose={closeCardClose}
					id_rango={data.id_rango}
					setIsOpen={setIsOpen}
				/>
			)}
			{loading && <SightLoading show={true} />}

			<InformationContainer
				// disabled={!data.en_uso && !isOpen}
				id={`process-edition-form-${data.id_rango}`}
				style={{
					borderRadius: 10,
					boxShadow: '0px 2px 7px #00000029',
					marginTop: 8,
					padding: 12,
					position: 'relative',
				}}
			>
				{!data.terminado && (
					<IconButton
						onClick={() => setIsOpen(!isOpen)}
						id={'botonRange'}
						size="small"
						sx={{ zIndex: 1, position: 'absolute', right: 12, top: 2 }}
					>
						{isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				)}
				{isOpen ? (
					<>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Prefijo
						</Typography>
						<TextField
							disabled
							fullWidth
							className="numberInput"
							size="small"
							sx={{ marginTop: '8px' }}
							type="number"
							value={data.prefijo_consecutivo}
						/>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 16 }}
						>
							Inicial
						</Typography>
						<Controller
							control={control}
							defaultValue={data.consecutivo_desde}
							name={`agw_ranges.${index}.consecutivo_desde`}
							rules={INITIAL_CONSECUTIVE_RULES}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className="numberInput"
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{ min: 0, max: 9999999 }}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="small"
									sx={{ marginTop: '8px' }}
									type="number"
									value={value}
								/>
							)}
						/>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 16 }}
						>
							Final
						</Typography>
						<Controller
							control={control}
							defaultValue={data.consecutivo_hasta}
							name={`agw_ranges.${index}.consecutivo_hasta`}
							rules={generateFinalConsecutiveRules(consecutivoDesde)}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className="numberInput"
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{ min: consecutivoDesde, max: 9999999 }}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="small"
									sx={{ marginTop: '8px' }}
									type="number"
									value={value}
									style={{ marginBottom: 12 }}
								/>
							)}
						/>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<IconButton onClick={() => setIsClose(true)}>
									<img src="/papelera.svg" alt="papelera" />
									<Typography
										color="#0D66B6"
										fontFamily="Montserrat"
										fontSize={14}
										fontWeight={600}
										textAlign="center"
										letterSpacing={0}
										marginLeft={1}
									>
										Eliminar
									</Typography>
								</IconButton>
							</Grid>
							<Grid item xs={6}>
								<Button
									onClick={() => {
										sincronizarRangosTest();
									}}
									style={{
										width: '147px',
										height: '32px',
										borderRadius: '8px',
										boxShadow: '0px 3px 3px #00000029',
										textAlign: 'center',
										fontSize: '12px',
										fontWeight: 600,
										fontFamily: 'Montserrat',
										textTransform: 'none',
										letterSpacing: 0,
									}}
									variant="contained"
								>
									Sincronizar a Test
								</Button>
							</Grid>
						</Grid>

						<div>
							<hr
								style={{
									borderTop: '1px solid rgb(112 103 112 / 28%)',
									marginLeft: '-13px',
									marginRight: '-13px',
								}}
							></hr>
							<InformationContainer
								id={`process-edition-form-client-${data.id_rango}`}
								style={{
									paddingTop: 6,
									position: 'relative',
								}}
							>
								<IconButton
									onClick={() => setIsClientOpen(!isClientOpen)}
									size="small"
									id={'botonClient'}
									sx={{ zIndex: 1, position: 'absolute', right: 12, top: 2 }}
								>
									{isClientOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
								</IconButton>
								{isClientOpen ? (
									<>
										<Typography
											color="#707070"
											fontFamily="Montserrat"
											fontSize={14}
											fontWeight={600}
										>
											Datos del cliente
										</Typography>
										<hr
											style={{
												borderTop: '1px solid rgb(112 103 112 / 28%)',
												marginLeft: '-13px',
												marginRight: '-13px',
												marginTop: '16px',
											}}
										></hr>
										<div style={{ marginTop: 16 }}>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
											>
												Cambiar Nombre o Razón Social
											</Typography>
											<Controller
												control={control}
												defaultValue={data.nombre}
												name={`agw_ranges.${index}.nombre`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														onChange={onChange}
														placeholder="Nombre o Razón Social"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Ciudad
											</Typography>
											<Controller
												control={control}
												name={`agw_ranges.${index}.ciudad`}
												render={({ field: { onChange } }) => (
													<Autocomplete
														options={cities}
														getOptionLabel={(option) => option.nombre}
														style={{ marginTop: 8 }}
														value={calculateValue(data.ciudad)}
														onChange={(_e, v) => {
															setError(`agw_ranges.${index}.ciudad`, {
																message: undefined,
															});
															if (typeof v === 'object') {
																setValue(
																	`agw_ranges.${index}.ciudad`,
																	v?.codigo,
																	{ shouldDirty: true }
																);
																setValue(
																	`agw_ranges.${index}.nombre_ciudad`,
																	v?.nombre,
																	{ shouldDirty: true }
																);
															}
														}}
														freeSolo
														renderInput={(params) => (
															<TextField
																{...params}
																size="small"
																onChange={onChange}
															/>
														)}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Dirección
											</Typography>
											<Controller
												control={control}
												defaultValue={data.direccion}
												name={`agw_ranges.${index}.direccion`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Dirección"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Teléfono
											</Typography>
											<Controller
												control={control}
												defaultValue={data.telefono}
												name={`agw_ranges.${index}.telefono`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Teléfono"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Correo Electrónico
											</Typography>
											<Controller
												control={control}
												defaultValue={data.correo}
												name={`agw_ranges.${index}.correo`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Correo Electrónico"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
										</div>
									</>
								) : (
									<Typography
										color="#707070"
										fontFamily="Montserrat"
										fontSize={14}
										fontWeight={600}
										textAlign="left"
										letterSpacing={0}
									>
										Datos del cliente
									</Typography>
								)}
							</InformationContainer>
						</div>
					</>
				) : (
					<Typography
						color="#707070"
						component="div"
						fontFamily="Montserrat"
						fontSize={14}
					>
						Prefijo: <b>{data.prefijo_consecutivo}</b>
						<br />
						Inicial:{' '}
						<Chip label={data.consecutivo_desde} size="small" variant="outlined" />{' '}
						Final:{' '}
						<Chip label={data.consecutivo_hasta} size="small" variant="outlined" />{' '}
					</Typography>
				)}
			</InformationContainer>
		</>
	);
};

export default AGWRangeHistoryCard;
